"use client";

import { generateTtsfx } from "@/app/_api/generateTtsfx";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { InfinityIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import type { SoundEffectEditorData } from "../state/actions";

import { LoopTooltip } from "../../TTSFX/LoopTooltip";
import { addSound, updateSoundName } from "../state/actions";
import { useSoundboard } from "../state/reducer";
import { AudioPlayerInstance } from "./AudioPlayerInstance";
import { Input } from "./Input";
import { MidiMappingConfigurator } from "./MidiConfigurator";
import { Panel } from "./Panel";
import { SaveFooter } from "./SaveFooter";

interface GeneratedSound {
  historyItemId?: string;
  id: string;
  isLooping: boolean;
  url: string;
}

const GeneratedSounds = ({
  generatedSounds,
  isLoading,
  buttonId,
  buttonIndex,
  soundName,
}: {
  buttonId: string;
  buttonIndex: number;
  generatedSounds: GeneratedSound[];
  isLoading: boolean;
  soundName: string;
}) => {
  const [playingId, setPlayingId] = useState<null | string>(null);
  const [recentlyAdded, setRecentlyAdded] = useState<Record<string, boolean>>({});
  const playerRef = useRef<AudioPlayerInstance | null>(null);
  const { state, dispatch } = useSoundboard();
  const t = useTranslations("ClientKeys.soundboard.sound_effect_editor");

  const toggleSound = useCallback(
    (sound: GeneratedSound) => {
      // If this sound is already playing, stop it
      if (playingId === sound.id) {
        playerRef.current?.stop();
        setPlayingId(null);
        return;
      }

      // Stop any currently playing sound
      if (playerRef.current) {
        playerRef.current.stop();
        playerRef.current.unload();
      }

      // Create and play the new sound
      playerRef.current = new AudioPlayerInstance(
        sound.url,
        {
          onPlay: () => setPlayingId(sound.id),
          onEnd: () => {
            if (!sound.isLooping) {
              setPlayingId(null);
            }
          },
          onStop: () => setPlayingId(null),
        },
        sound.isLooping,
      );
      playerRef.current.play();
    },
    [playingId],
  );

  const handleAddSound = useCallback(
    (sound: GeneratedSound) => {
      dispatch(
        addSound({
          presetId: state.activePresetId!,
          slotIndex: buttonIndex,
          sound: {
            id: sound.id,
            name: soundName,
            type: "sound",
            url: sound.url,
            historyItemId: sound.historyItemId,
          },
        }),
      );

      // Mark this sound as recently added
      setRecentlyAdded(prev => ({ ...prev, [sound.id]: true }));

      // Reset the recently added state after 1 second
      setTimeout(() => {
        setRecentlyAdded(prev => ({ ...prev, [sound.id]: false }));
      }, 1000);
    },
    [dispatch, state.activePresetId, buttonIndex, soundName],
  );

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-8'>
        {isLoading ? (
          <div className='flex animate-spin items-center justify-center'>
            <Spinner20 />
          </div>
        ) : (
          <>
            {generatedSounds.length > 0 && (
              <span className='font-soundboard f-ui-01 my-12 text-sm font-medium text-light'>
                {t("generated_sounds")}
              </span>
            )}
            {generatedSounds.map((sound, index) => (
              <div className='flex items-center gap-8' key={sound.id}>
                <button
                  className='flex aspect-1x1 h-36 items-center justify-center rounded-sm border border-default px-12 py-4 transition-colors'
                  onClick={() => toggleSound(sound)}
                >
                  {playingId === sound.id ? <Pause20 /> : <Play20 />}
                </button>
                <div className='flex h-36 flex-grow items-center rounded-sm border border-default px-12 py-4'>
                  <p className='font-soundboard f-ui-03 text-sm text-default'>{`Generation ${index + 1}`}</p>
                </div>
                <button
                  className='font-soundboard f-ui-03 h-36 rounded-sm border px-12 py-4 text-default disabled:opacity-50'
                  disabled={recentlyAdded[sound.id]}
                  onClick={() => handleAddSound(sound)}
                >
                  {recentlyAdded[sound.id] ? t("added_to_pad", { padNumber: buttonId }) : t("add")}
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export const SoundEffectEditor = ({ border = true }: { border?: boolean }) => {
  const t = useTranslations("ClientKeys.soundboard.sound_effect_editor");
  const tTtsfx = useTranslations("ClientKeys.TTSFX.widget");
  const { state, dispatch } = useSoundboard();
  const panelData = state.panel?.data as SoundEffectEditorData;

  const [description, setDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [generatedSounds, setGeneratedSounds] = useState<GeneratedSound[]>([]);
  const [soundName, setSoundName] = useState(panelData.soundName || t("custom_generation"));

  const handleGenerate = useCallback(async () => {
    if (!description.trim() || isGenerating) return;

    setIsGenerating(true);
    try {
      const authToken = await state.user?.getIdToken();
      const generations = await generateTtsfx({
        source: "soundboard",
        config: { text: description, loop: isLooping },
        token: authToken,
      });
      const timestamp = Date.now();
      setGeneratedSounds(
        generations.map((generation, index) => ({
          historyItemId: generation.sound_generation_history_item?.sound_generation_history_item_id,
          id: `generation-${timestamp}-${index}`,
          url: `data:audio/opus;base64,${generation.waveform_base_64}`,
          isLooping,
        })),
      );
      setSoundName(description.slice(0, 20));
    } finally {
      setIsGenerating(false);
    }
  }, [description, isGenerating, state.user, isLooping]);

  useEffect(() => {
    setSoundName(panelData.soundName || t("custom_generation"));
  }, [panelData.soundName, t]);

  const handleSoundNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSoundName = e.target.value;
    if (panelData.soundUrl) {
      dispatch(
        updateSoundName({
          presetId: state.activePresetId!,
          slotIndex: panelData.buttonIndex,
          soundName: newSoundName,
        }),
      );
    }
    setSoundName(newSoundName);
  };

  return (
    <Panel
      border={border}
      footer={<SaveFooter preset={state.presets[state.activePresetId!]} />}
      subtitle={t("panel_subtitle", { padNumber: panelData.id })}
      title={t("panel_title")}
    >
      <div className='flex flex-col gap-16'>
        {/* Description input */}
        <div className='flex flex-col gap-8'>
          <label className='font-soundboard f-ui-01 mb-12 text-sm font-medium text-light' htmlFor='effect-description'>
            {t("title")}
          </label>
          <textarea
            className='font-soundboard h-160 w-full resize-none rounded-sm bg-dark p-12 text-sm text-default'
            id='effect-description'
            onChange={e => setDescription(e.target.value)}
            placeholder={t("description_placeholder")}
            value={description}
          />
          <div className='flex gap-2'>
            <button
              className='font-soundboard f-ui-03 flex-1 rounded-sm bg-inverse px-16 py-8 text-sm text-inverse disabled:opacity-50'
              disabled={!description.trim() || isGenerating}
              onClick={handleGenerate}
            >
              {isGenerating ? t("generating") : t("generate")}
            </button>
            <LoopTooltip>
              <button
                aria-label={isLooping ? t("loop_on") : t("loop_off")}
                aria-pressed={isLooping}
                className='font-soundboard f-ui-03 flex min-w-84 items-center justify-center gap-8 rounded-sm bg-darker px-8 py-8 text-sm'
                onClick={() => setIsLooping(!isLooping)}
              >
                <InfinityIcon size={16} />
                {isLooping ? tTtsfx("on") : tTtsfx("off")}
              </button>
            </LoopTooltip>
          </div>
        </div>
        {(generatedSounds.length > 0 || panelData.soundUrl) && (
          <Input
            label={t("sound_name_label")}
            onChange={handleSoundNameChange}
            placeholder={t("sound_name_placeholder")}
            value={soundName}
          />
        )}
        <GeneratedSounds
          buttonId={panelData.id}
          buttonIndex={panelData.buttonIndex}
          generatedSounds={generatedSounds}
          isLoading={isGenerating}
          soundName={soundName}
        />
        <MidiMappingConfigurator buttonIndex={panelData.buttonIndex} />
      </div>
    </Panel>
  );
};
